import { Box, Button, Stack } from "@mui/material"
import { RefreshTaskHeader } from "event-definitions"
import { noop } from "lib/noop"
import { Case, CaseElse, If, Switch } from "lib/@components/switch"
import Iconify from "minimals-template/components/Iconify"
import { useEffect, useState } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { OnlyWithoutComparisonReadOnly } from "./only-without-comparison-read-only"

export function Status({ path, copy = noop, revert = noop, onCopyChanged = noop, ...props }) {
    const { to } = useBoundContext()
    const [value1] = useBoundValue(path)

    const value2 = Object.get(to, path, true)
    const copied = Object.isEqual(value1, value2)
    useEffect(() => {
        onCopyChanged(copied)
    }, [copied, onCopyChanged])

    return !!path && <StatusButton copied={copied} copy={copy} revert={revert} {...props} />
}

function doAndRefresh(fn) {
    return () => {
        fn()
        RefreshTaskHeader.raiseOnce()
    }
}

export function StatusButton({ copy = noop, show = true, copied, onHover = noop, revert = noop, caption }) {
    const { compareType, noIndividualEditor, canSave } = useBoundContext()
    const [hover, setHover] = useState(false)
    useEffect(() => {
        onHover(hover)
        return () => onHover(false)
    }, [hover, onHover])
    return (
        canSave && (
            <OnlyWithoutComparisonReadOnly>
                <Box
                    sx={{ whiteSpace: "nowrap" }}
                    alignSelf="stretch"
                    display="flex"
                    alignItems="center"
                    overflow="visible"
                    justifyContent="center"
                    flexDirection="column"
                >
                    <Box width={2} height={0}>
                        <Box height={0} overflow="visible">
                            <Box width={2} height={200000} my={-400} flex={1} bgcolor="action.hover" />
                        </Box>
                    </Box>
                    <Box borderRadius={1} px={2}>
                        <Box
                            sx={{
                                alignSelf: "center",
                                width: 80,
                                color: "text.secondary",
                            }}
                        >
                            {show && compareType !== "sameConflict" && (
                                <If
                                    truthy={copied}
                                    then={
                                        <Stack sx={{ mx: 1 }} spacing={1} alignItems="center">
                                            <Box>
                                                <Box>
                                                    {!noIndividualEditor && (
                                                        <Button
                                                            onMouseEnter={() => setHover(false)}
                                                            onMouseLeave={() => setHover(false)}
                                                            size="small"
                                                            sx={{ bgcolor: "background.paper" }}
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={doAndRefresh(revert)}
                                                            data-cy="compare-undo-button"
                                                            startIcon={
                                                                <Iconify
                                                                    sx={{ width: 16, height: 16 }}
                                                                    icon="ci:redo"
                                                                />
                                                            }
                                                        >
                                                            Undo
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Stack>
                                    }
                                    else={
                                        <Stack sx={{ mx: 1 }} spacing={1} alignItems="center">
                                            <Box>
                                                {!noIndividualEditor && (
                                                    <Button
                                                        size="small"
                                                        onMouseEnter={() => setHover(true)}
                                                        onMouseLeave={() => setHover(false)}
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ bgcolor: "background.paper" }}
                                                        onClick={doAndRefresh(copy)}
                                                        data-cy="compare-copy-button"
                                                        startIcon={
                                                            <Iconify
                                                                sx={{ width: 16, height: 16 }}
                                                                icon="ic:round-arrow-back"
                                                            />
                                                        }
                                                    >
                                                        {caption || (
                                                            <Switch value={compareType}>
                                                                <Case when="conflict">Use</Case>
                                                                <Case when="localChanged">Revert</Case>
                                                                <Case when="remoteChanged">Use</Case>
                                                                <CaseElse>Copy</CaseElse>
                                                            </Switch>
                                                        )}
                                                    </Button>
                                                )}
                                            </Box>
                                        </Stack>
                                    }
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            </OnlyWithoutComparisonReadOnly>
        )
    )
}
